
import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import './AssigningProject.css';
import { API_URL } from './API';

const AssigningEmployeeProject = ({ project, onClose, onEmployeeAdded }) => {
  const [formData, setFormData] = useState({
    employeeId: '',
    employeeName: '',
    role: '',
    location: project?.location || '',
    phoneNo: '',
    assignedBy: '',
    assignedDate: new Date().toISOString().split('T')[0],
    status: 'Active'
  });

  const [employees, setEmployees] = useState([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (project?.location) {
      setFormData(prev => ({
        ...prev,
        location: project.location
      }));
    }
  }, [project]);

  const fetchEmployees = async () => {
    setIsLoadingEmployees(true);
    setErrorMessage('');

    try {
      const response = await fetch(`${API_URL}/employees/get_employees`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
      });

      if (!response.ok) throw new Error(`Failed to fetch employees: ${response.status}`);

      const result = await response.json();

      let employeesData = [];

      if (Array.isArray(result)) employeesData = result;
      else if (result.employees) employeesData = result.employees;
      else if (result.data) employeesData = result.data;

     
      employeesData = employeesData.filter(
        emp => emp.status?.toLowerCase() === "active"
      );

      setEmployees(employeesData);
    } catch (error) {
      setErrorMessage('Failed to load employees');
      setEmployees([]);
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  const handleEmployeeSelect = (e) => {
    const selectedId = e.target.value;
    const selectedEmployee = employees.find(
      emp => (emp.employee_id || emp.id).toString() === selectedId
    );

    if (selectedEmployee) {
      setFormData(prev => ({
        ...prev,
        employeeId: selectedEmployee.employee_id || selectedEmployee.id,
        employeeName: selectedEmployee.name || '',
        phoneNo: selectedEmployee.phone || selectedEmployee.contact_number || ''
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const payload = {
        employee_id: parseInt(formData.employeeId) || null,
        project_id: parseInt(project?.project_id || project?.id) || null,
        employee_name: formData.employeeName,
        assigned_by: formData.assignedBy,
        location: formData.location,
        role: formData.role,
        phone_number: formData.phoneNo,
        assignment_date: formData.assignedDate,
        ending_date: null
      };

      const response = await fetch(`${API_URL}/projects/create_employee_to_project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message);

      const notification = {
        id: Date.now(),
        type: 'project_assignment',
        message: `You have been assigned to project: ${project?.project_name || project?.name}`,
        details: {
          project: project?.project_name || project?.name,
          assignedBy: formData.assignedBy || 'Admin',
          location: project?.location || formData.location,
          role: formData.role
        },
        timestamp: new Date().toISOString(),
        read: false
      };

      const existingNotifications = JSON.parse(
        localStorage.getItem(`notifications_${formData.employeeId}`) || '[]'
      );

      existingNotifications.unshift(notification);

      localStorage.setItem(
        `notifications_${formData.employeeId}`,
        JSON.stringify(existingNotifications)
      );

      window.dispatchEvent(new Event('notificationUpdate'));

      const newEmployee = {
        id: String(formData.employeeId),
        employee_id: String(formData.employeeId),
        name: formData.employeeName,
        role: formData.role,
        location: formData.location,
        phone: formData.phoneNo,
        assignedBy: formData.assignedBy,
        assignedDate: formData.assignedDate,
        status: 'Active',
        project_id: String(project?.project_id || project?.id)
      };

      if (onEmployeeAdded) onEmployeeAdded(newEmployee);

      alert('Employee assigned successfully!');
      onClose();
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="ASSG-00">
      <div className="ASSG-01">
        <div className="ASSG-02">
          <h3>Add Employee to Project</h3>
          <FaTimes className="ASSG-03" onClick={onClose} />
        </div>

        {errorMessage && (
          <div className="error-alert" style={{
            padding: '10px',
            margin: '10px 0',
            backgroundColor: '#fee',
            color: '#c33',
            borderRadius: '4px'
          }}>
            {errorMessage}
          </div>
        )}

        <div className="ASSG-04">
          <div className="ASSG-05">
            <div className="ASSG-06">
              <label>Employee Name</label>
              <select
                name="employeeId"
                value={formData.employeeId}
                onChange={handleEmployeeSelect}
                disabled={isLoadingEmployees}
              >
                <option value="">
                  {isLoadingEmployees ? 'Loading employees...' : 'Select Employee'}
                </option>

                {employees.map(emp => (
                  <option key={emp.employee_id || emp.id} value={emp.employee_id || emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="ASSG-06">
              <label>Employee ID</label>
              <input
                type="text"
                value={formData.employeeId}
                readOnly
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>
          </div>

          <div className="ASSG-05">
            <div className="ASSG-06">
              <label>Role</label>
              <input
                type="text"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
              />
            </div>

            <div className="ASSG-06">
              <label>Location</label>
              <input
                type="text"
                value={formData.location}
                readOnly
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>
          </div>

          <div className="ASSG-05">
            <div className="ASSG-06">
              <label>Phone Number</label>
              <input
                type="tel"
                name="phoneNo"
                value={formData.phoneNo}
                onChange={handleInputChange}
              />
            </div>

            <div className="ASSG-06">
              <label>Assigned By</label>
              <input
                type="text"
                name="assignedBy"
                value={formData.assignedBy}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="ASSG-05">
            <div className="ASSG-06">
              <label>Assignment Date</label>
              <input
                type="date"
                name="assignedDate"
                value={formData.assignedDate}
                onChange={handleInputChange}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          <div className="ASSG-07">
            <button className="ASSG-08" onClick={onClose} disabled={isSubmitting}>Cancel</button>
            <button className="ASSG-09" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssigningEmployeeProject;
